<template>
    <div>
        <Toolbar>
            <BackButton :to="__('subscriptions.invoices')"
                        :href="$route('subscriptions.customers.invoices', invoice.customer.uid)"
            />

            <template v-slot:right>
                <div class="btn-group">
                    <a v-if="invoice.status === 'unpaid' && invoice.payment_url"
                       :href="invoice.payment_url"
                       class="btn btn-bg-white btn-text-dark-50 btn-icon-primary btn-hover-primary font-weight-bold px-5"
                       target="_new"
                    >
                        <i class="fas fa-hand-holding-usd"/>
                        {{ __('actions.pay_thing', {thing: __('subscriptions.invoice')}) }}
                    </a>

                    <button
                        class="btn btn-bg-white btn-text-dark-50 btn-icon-primary btn-hover-primary font-weight-bold px-5"
                        @click="downloadDocument"
                        :disabled="!invoice.document_url"
                    >
                        <i class="fas fa-download"/>
                        {{ __('actions.download_thing', {thing: __('subscriptions.invoice')}) }}
                    </button>
                </div>
            </template>
        </Toolbar>

        <Card class="py-xl-5" slim-content>
            <template v-slot:title>
                {{ __('subscriptions.invoice') }}: {{ invoice.display_name }}
            </template>

            <div class="d-flex flex-column gap-3">
                <div class="d-flex flex-row justify-content-between text-center bg-light-light rounded px-5 py-10">
                    <div class="flex-1">
                        <div class="font-weight-light text-muted">{{ __('general.invoice_number') }}</div>
                        <div>#{{ invoice.id }}</div>
                    </div>
                    <div class="flex-1">
                        <div class="font-weight-light text-muted">{{ __('general.date') }}</div>
                        <div>{{ $localDate(invoice.created_at) }}</div>
                    </div>
                    <div class="flex-1">
                        <div class="font-weight-light text-muted">{{ __('general.status') }}</div>
                        <div>
                            <Badge v-if="invoice.status === 'paid'" icon="fas fa-check" type="badge-success">
                                {{ __('subscriptions.invoice_status.paid') }}
                            </Badge>
                            <Badge v-else icon="fas fa-times" type="badge-warning" class="text-white">
                                {{ __('general.thing.pending', {thing: __('general.payment')}) }}
                            </Badge>
                        </div>
                    </div>
                </div>

                <Alert v-if="invoice.total === 0" title="Hinweis" type="info">
                    <template v-slot:icon>
                        <i class="fas fa-info-circle"/>
                    </template>
                    {{ __('subscriptions.disclaimer.no_invoice_for_zero_total') }}
                </Alert>

                <div v-if="invoice.usage_entries" class="row">
                    <div v-for="usageEntriesOfStore in useEntriesByStore"
                         :key="usageEntriesOfStore[0].store_uuid"
                         class="col"
                         :class="{
                             'col-xl-6': Object.keys(useEntriesByStore).length > 1,
                         }"
                    >
                        <StoreSaleChannelUsageWidget class="card card-custom card-body bg-light-primary shadow-none p-5"
                                                     :usage-data-entries="usageEntriesOfStore"
                                                     :range="invoice.usage_data.range"
                        />
                    </div>
                </div>

                <div class="table-responsive">
                    <table class="table table-vertical-center m-0">
                        <!-- SUBSCRIPTION BASE PRICE -->
                        <thead v-if="invoice.customer.subscriptions">
                        <tr>
                            <th style="max-width: 40%;">{{ __('general.name') }}</th>
                            <th class="text-center">{{ __('general.units') }}</th>
                            <th class="text-center">{{ __('general.per_unit') }}</th>
                            <th class="text-right">{{ __('general.total') }}</th>
                        </tr>
                        </thead>
                        <tbody
                            v-if="invoice.customer.subscriptions && invoice.customer.subscriptions[0].pricing_conditions.length > 0">
                        <tr v-for="pricingCondition in invoice.customer.subscriptions[0].pricing_conditions">
                            <td class="max-w-50">
                                {{ pricingCondition.name }}
                            </td>
                            <td class="text-center">
                                {{ pricingCondition.units }}
                            </td>
                            <td class="text-center">
                                <Price :value="pricingCondition.per_unit"/>
                            </td>
                            <td class="text-right">
                                <Price :value="pricingCondition.total"/>
                            </td>
                        </tr>
                        </tbody>

                        <!-- ADDONS -->
                        <thead v-if="invoice.addons && invoice.addons.length > 0">
                        <tr>
                            <th style="max-width: 40%;">{{ __('general.name') }}</th>
                            <th class="text-center">{{ __('general.units') }}</th>
                            <th class="text-center">{{ __('general.per_unit') }}</th>
                            <th class="text-right">{{ __('general.total') }}</th>
                        </tr>
                        </thead>
                        <tbody v-if="invoice.addons">
                        <tr v-for="addon in invoice.addons">
                            <td>
                                <Productable :productable="addon"/>
                            </td>
                            <td class="text-center">
                                <div v-if="invoice.type === 'addons'" class="text-center">
                                    {{ invoice.subscription.paid_stores_count }}
                                </div>
                            </td>
                            <td class="text-center">
                                <div v-if="invoice.type === 'addons'" class="text-center">
                                    <Price :value="addon.pricings[0].value"/>
                                </div>
                            </td>
                            <td class="text-right">
                                <div v-if="invoice.type === 'addons'">
                                    <Price
                                        :value="addon.pricings[0].value * (invoice.subscription.paid_stores_count ?? 1)"/>
                                </div>
                            </td>
                        </tr>
                        </tbody>

                        <!-- DYNAMIC USAGE -->
                        <thead v-if="invoice.usage_entries">
                        <tr>
                            <th style="max-width: 40%;">{{ __('general.name') }}</th>
                            <th class="text-center">
                                <InfoPopover :label="__('general.charged_transaction_volume')" append>
                                    The sum of all transactions for the given sales channel in the timespan.
                                    Cents are rounded down.
                                </InfoPopover>
                            </th>
                            <th class="text-center">{{ __('general.per_unit') }}</th>
                            <th class="text-right">{{ __('general.total') }}</th>
                        </tr>
                        </thead>
                        <tbody v-if="invoice.usage_entries">
                        <tr v-for="usageEntry in invoice.usage_entries">
                            <td>
                                {{ usageEntry.item }}
                                <Link
                                    as="a"
                                    :href="$route('orders.index.store', {
                                        storeUuid: usageEntry.store_uuid,
                                        tab:'done',
                                        integration: usageEntry.integration,
                                        range: invoice.usage_data.range,
                                        states: BillableOrderStates.join(',')
                                    })">
                                    {{ __('actions.view_thing', {thing: __('orders.orders')}) }}
                                </Link>
                            </td>
                            <td class="text-center">
                                {{ usageEntry.quantity }} (EUR)
                            </td>
                            <td class="text-center">
                                <Price :value="usageEntry.unit_amount_decimal / 100"
                                       :decimals="4"
                                       no-padding
                                />
                            </td>
                            <td class="text-right">
                                <Price :value="usageEntry.quantity / 100 * parseFloat(usageEntry.unit_amount_decimal)"/>
                            </td>
                        </tr>
                        </tbody>

                        <!-- SUMS -->
                        <tbody>
                        <tr>
                            <td class="text-right" colspan="3">
                                {{ __('general.total_without_tax') }}
                            </td>
                            <td class="text-right">
                                <Price :value="invoice.total_net"/>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-right" colspan="3">
                                {{ __('general.vat_with_value', {value: 19}) }}
                            </td>
                            <td class="text-right">
                                <Price :value="invoice.total_net * invoice.tax_rate"/>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-right font-weight-boldest" colspan="3">
                                {{ __('general.total') }}
                            </td>
                            <td class="text-right font-weight-boldest">
                                <Price :value="invoice.total"/>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Card>
    </div>
</template>

<script>
import BackButton from "@components/BackButton.vue";
import Productable from "@components/Subscriptions/Productable.vue";
import PricingGroup from "@components/Subscriptions/Pricing/PricingGroup.vue";
import Price from "@components/Price.vue";
import Badge from "@components/Badge.vue";
import Card from "@components/Cards/Card.vue";
import Toolbar from "@components/Toolbar.vue";
import WebsocketMixin from "@mixins/WebsocketMixin";
import Alert from "@components/Alert.vue";
import StoreSaleChannelUsageWidget from "@components/Widgets/Invoices/StoreSaleChannelUsageWidget.vue";
import InfoPopover from "@components/InfoPopover.vue";
import { Link } from '@inertiajs/vue2';
import OrderStatesMixin from "@mixins/orderStatesMixin";

export default {
    components: {
        InfoPopover, Link, StoreSaleChannelUsageWidget, Alert, Toolbar, Card, Badge,
        Price, PricingGroup, Productable, BackButton
    },

    mixins: [WebsocketMixin, OrderStatesMixin],

    props: {
        invoice: Object,
        customer: Object,
        address: Object,
    },

    mounted() {
        this.listenUser(this.invoice.customer.uid, 'invoice.updated', this.invoiceUpdated);
    },

    methods: {
        downloadDocument() {
            if (this.invoice.document_url) {
                window.open(this.invoice.document_url, '_blank').focus();
            }
        },

        invoiceUpdated(invoice) {
            if (invoice.id === this.invoice.id) {
                this.$inertia.reload({only: ['invoice']})
            }
        },

        groupByStore(entries) {
            return Object.groupBy(entries, (entry) => entry.store_uuid);
        }
    },

    computed: {
        useEntriesByStore() {
            return this.groupByStore(this.invoice.usage_entries)
        },

        total() {
            let total = 0;
            if (this.invoice.customer.subscriptions) {
                for (let subscription of this.invoice.customer.subscriptions) {
                    for (let pricingCondition of subscription.pricing_conditions) {
                        total += pricingCondition.total;
                    }
                }
            }
            return total;
        }
    }
}
</script>

<style>
.max-w-50 {
    max-width: 50%;
}

td {
    border-top-style: dotted !important;
}
</style>
